import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";

import { getAuthorizationToken } from "@/auth/tokenStore";

import { calculateAge, formatCalendarDate } from "../../Utils/helper";
import avatarImage from "../../assets/images/avatar.svg";
import birthdayIcon from "../../assets/images/birthdayicon.svg";
import incidentIcon from "../../assets/images/incident.svg";

import "./PinnedCases.css";

const SLOT_COUNT = 6;
const GAP = 5;
const CARD_SPACING = 45; // 25px number, two 5px gaps, and 5px side padding.
const fullName = (client) => [client?.last_name, client?.first_name].filter(Boolean).join(", ");

const Chats = ({ onHeightChange }) => {
  const origin = process.env.REACT_APP_BACKEND_URL;
  const token = getAuthorizationToken();
  const [pinnedCases, setPinnedCases] = useState(() => Array(SLOT_COUNT).fill(null));
  const [layout, setLayout] = useState({ columns: 1, nameWidth: 0, dateWidth: 0, stacked: false });
  const containerRef = useRef(null);
  const heightCallbackRef = useRef(onHeightChange);
  const reportedHeightRef = useRef(null);

  useLayoutEffect(() => {
    heightCallbackRef.current = onHeightChange;
  }, [onHeightChange]);

  const reportHeight = useCallback(() => {
    if (!containerRef.current) return;
    const height = Math.ceil(containerRef.current.getBoundingClientRect().height) + GAP;
    if (height !== reportedHeightRef.current) {
      reportedHeightRef.current = height;
      heightCallbackRef.current?.(height);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    axios
      .get(`${origin}/api/home/pinned-cases/`, {
        headers: { Authorization: token },
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (!controller.signal.aborted) {
          setPinnedCases(
            Array.from({ length: SLOT_COUNT }, (_, index) => data?.[`p${index + 1}`] ?? null)
          );
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) console.error(error);
      });
    return () => controller.abort();
  }, [origin, token]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const context = document.createElement("canvas").getContext("2d");
    let frame;
    let disposed = false;

    const measure = () => {
      if (disposed) return;
      const style = window.getComputedStyle(container);
      if (context) context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const textWidth = (value) => context?.measureText(value || "").width ?? 0;
      let nameWidth = 0;
      let dateWidth = 0;

      pinnedCases.forEach((pinnedCase) => {
        if (!pinnedCase) return;
        const client = pinnedCase.for_client;
        nameWidth = Math.max(
          nameWidth,
          client ? textWidth(fullName(client)) + 20 : 0,
          textWidth(pinnedCase.case_type?.name) + (pinnedCase.case_type?.casetype_icon ? 20 : 0)
        );
      });

      // These tracks use max-content, so their actual widths include the icons,
      // tabular date digits, age label, and age value even in the stacked layout.
      container.querySelectorAll(".home-pinned-dates").forEach((dates) => {
        const birthdayWidth = dates
          .querySelector(".home-pinned-birthday")
          .getBoundingClientRect().width;
        const incidentWidth = dates
          .querySelector(".home-pinned-incident")
          .getBoundingClientRect().width;
        const ageWidth =
          dates.querySelector(".home-pinned-age")?.getBoundingClientRect().width ?? 0;
        dateWidth = Math.max(dateWidth, Math.max(birthdayWidth, incidentWidth) + ageWidth + GAP);
      });

      nameWidth = Math.ceil(nameWidth);
      dateWidth = Math.ceil(dateWidth);
      const itemWidth = nameWidth + dateWidth + CARD_SPACING;
      const width = container.clientWidth;
      const next = {
        columns: Math.max(1, Math.min(SLOT_COUNT, Math.floor((width + GAP) / (itemWidth + GAP)))),
        nameWidth,
        dateWidth,
        stacked: !context || width < itemWidth,
      };
      setLayout((previous) =>
        Object.keys(next).every((key) => previous[key] === next[key]) ? previous : next
      );
      reportHeight();
    };
    const scheduleMeasure = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measure);
    };
    measure();
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(scheduleMeasure);
    observer?.observe(container);
    window.addEventListener("resize", scheduleMeasure);
    document.fonts?.ready.then(scheduleMeasure);
    return () => {
      disposed = true;
      observer?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
      window.cancelAnimationFrame(frame);
    };
  }, [pinnedCases, reportHeight]);

  useLayoutEffect(reportHeight, [layout, pinnedCases, reportHeight]);

  return (
    <div className="content-top home-pinned-cases">
      <div
        ref={containerRef}
        className={`home-pinned-cases-grid${layout.stacked ? " is-stacked" : ""}`}
        style={{
          "--pinned-columns": layout.columns,
          "--pinned-name-width": `${layout.nameWidth}px`,
          "--pinned-date-width": `${layout.dateWidth}px`,
        }}
      >
        {pinnedCases.map((pinnedCase, index) => {
          const client = pinnedCase?.for_client;
          const age = calculateAge(client?.birthday);
          const row = Math.floor(index / layout.columns);
          const column = index % layout.columns;
          return (
            <div
              key={index}
              className={`home-pinned-case${pinnedCase ? "" : " is-empty"}`}
              style={{
                backgroundColor:
                  (row + column) % 2 === 0 ? "var(--primary-10)" : "var(--primary-4)",
              }}
            >
              {pinnedCase && (
                <div className="home-pinned-identity">
                  <div className="home-pinned-line">
                    {client && (
                      <img
                        src={client.profile_pic_29p || avatarImage}
                        className="home-pinned-avatar theme-ring"
                        alt=""
                        width={15}
                        height={15}
                      />
                    )}
                    <span>{fullName(client)}</span>
                  </div>
                  <div className="home-pinned-line">
                    {pinnedCase.case_type?.casetype_icon && (
                      <img src={pinnedCase.case_type.casetype_icon} alt="" width={15} height={15} />
                    )}
                    <span>{pinnedCase.case_type?.name}</span>
                  </div>
                </div>
              )}
              <span className="home-pinned-number">{index + 1}</span>
              {pinnedCase && (
                <div className="home-pinned-dates">
                  <div className="home-pinned-line home-pinned-birthday">
                    {client && <img src={birthdayIcon} alt="" width={15} height={15} />}
                    <span>{formatCalendarDate(client?.birthday)}</span>
                  </div>
                  {age !== "" && (
                    <div className="home-pinned-age">
                      <span className="text-primary-25">age</span>
                      <span>{age}</span>
                    </div>
                  )}
                  <div className="home-pinned-line home-pinned-incident">
                    {pinnedCase.incident_date && (
                      <img src={incidentIcon} alt="" width={15} height={15} />
                    )}
                    <span>{formatCalendarDate(pinnedCase.incident_date)}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Chats;
